import React, {
  useCallback,
  useMemo,
  useState,
} from 'react'

import PropTypes from 'prop-types'

import DisplayManagerContext from './DisplayManagerContext'
import useFocusRestoration from './hooks/useFocusRestoration'

const NULL_UI = {}


export default function DisplayManager({ children }) {
  const [uiStack, setUIStack] = useState([])

  const pushActiveUI = useCallback((name, props) => {
    // Capture the focused element's `data-focus-id` now, while it's still the
    // active element. When this view closes we re-query the DOM for it and
    // hand focus back, since the triggering element itself is unmounted while
    // the view is open. See `useFocusRestoration`.
    //
    const restoreFocusKey = document.activeElement?.getAttribute('data-focus-id')

    setUIStack((stack) => stack.concat({
      name,
      props,
      restoreFocusKey,
    }))
  }, [])

  const mergeActiveUIProps = useCallback((props) => {
    setUIStack((stack) => {
      const activeUI = stack.pop()

      activeUI.props = {
        ...activeUI.props,
        ...props,
      }

      return [
        ...stack,
        activeUI,
      ]
    })
  }, [])

  const popActiveUI = useCallback(() => {
    setUIStack((stack) => stack.slice(0, -1))
  }, [])

  const closeAllUI = useCallback(() => {
    setUIStack([])
  }, [])

  useFocusRestoration({ uiStack })

  const value = useMemo(() => {
    const lastIdx = uiStack.length - 1
    const activeUI = uiStack[lastIdx] || NULL_UI

    return {
      activeUI,
      closeAllUI,
      mergeActiveUIProps,
      popActiveUI,
      pushActiveUI,
    }
  }, [closeAllUI, mergeActiveUIProps, popActiveUI, pushActiveUI, uiStack])

  return (
    <DisplayManagerContext.Provider
      value={ value }
    >
      { children }
    </DisplayManagerContext.Provider>
  )
}

DisplayManager.propTypes = {
  children: PropTypes.node,
}
